import React from 'react';

import cs from 'classnames';
import {ProcedureTaskCore, ProcessTaskCore} from 'sweet-taskinfo';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import StateButton from 'app/general/widgets/StateButton';
import {ForegroundDiv} from 'app/procedures/styles';
import {useNotifications} from 'app/toasts/hooks';
import {gt} from 'app/utils/i18n';

import {TaskInstanceSerializedType} from 'sweet-types';

type Props = {
  taskCore: ProcedureTaskCore | ProcessTaskCore;
  className: string;
  isTaskBlocked?: boolean;
  hashid?: string;
  small?: boolean;
  updateTaskInstance: (
    task: TaskInstanceSerializedType,
    changes: any
  ) => Promise<any>;
  onBlockedClick?: () => void;
};

export function MarkCompletedButton({
  taskCore,
  className = '',
  hashid,
  small = false,
  updateTaskInstance,
  onBlockedClick,
  isTaskBlocked = false,
}: Props): JSX.Element {
  const {showToastNotification} = useNotifications();
  const completed: boolean = taskCore.isCompleted === true && !isTaskBlocked;
  const canToggle: boolean =
    !isTaskBlocked &&
    (completed ||
      (taskCore.hasVersion === true
        ? taskCore.state.progressInfo.canBeCompleted
        : true));

  const task: TaskInstanceSerializedType = taskCore.task;

  const toggle = () =>
    updateTaskInstance(task, taskCore.payload.toSetCompleted(!completed)).then(
      (result) => {
        if (hashid == null) {
          showToastNotification(
            completed
              ? gt('Task marked as incomplete')
              : gt('Task marked as complete'),
            {
              level: completed != null ? 'info' : 'success',
            }
          );
        }

        return result;
      }
    );

  return (
    <LazyTooltip
      hide={canToggle}
      tooltip={
        isTaskBlocked
          ? gt(
              'Complete previous steps in the process task to mark this procedure as completed'
            )
          : gt(
              'Finish the highlighted step(s) before marking the task as complete'
            )
      }
    >
      <ForegroundDiv
        className={`d-inline-block d-print-none ${className}`}
        onClick={() => !canToggle && onBlockedClick && onBlockedClick()}
      >
        <LazyTooltip
          hide={completed == null}
          tooltip={
            !completed
              ? gt('Click to mark the task as complete')
              : gt('Click to mark the task as incomplete')
          }
        >
          <StateButton
            onClick={toggle}
            disabled={!canToggle}
            busyText={gt('Updating...')}
            className={cs('btn-primary', {
              active: completed,
              'btn-sm': small,
            })}
          >
            {!completed ? gt('Mark Complete') : gt('Completed')}
          </StateButton>
        </LazyTooltip>
      </ForegroundDiv>
    </LazyTooltip>
  );
}
